import React, { useEffect, useState, useRef, useCallback, useContext, useMemo } from "react";
import ApiErrorMessages from "../components/UI/ApiErrorMessages";
import Button from "../components/UI/Button";
import ErrorMessages from "../components/UI/ErrorMessages";
import Loader from "../components/UI/Loader";
import Select from "../components/UI/Select";
import { ASSESSMENT_ID, DELIVERY_ID, Mapping } from "../constants/formTypes";
import useElementVisible from "../hooks/useElementVisible";
import Form from "../models/serviceModels/Form";
import FormInstance from "../models/serviceModels/FormInstance";
import { createFormInstance, deleteFormInstance, getForm, searchFormsInstances } from "../services/formsService";
import { formatJSONDateWithoutTime } from "../utils/dateHelpers";
import { useOutletContext, useParams } from "react-router-dom";
import { ACTIVITY_DATE_ID } from "../constants/fieldTypes";
import { executeSearch, getDefaultSearchFilters, getSearchFields } from "../services/searchService";
import { TRACKING_HISTORY_SEARCH } from "../constants/searchType";
import SearchFieldReference from "../models/serviceModels/SearchFieldReference";
import DefaultSearchFilter from "../models/serviceModels/DeafultSearchFilter";
import Search, { SearchEvent } from "../components/UI/Search";
import { format } from "date-fns";
import { MeContext } from "../App";
import Modal from "../components/UI/Modal";
import Dropdown from "../components/UI/Dropdown";
import { ADMIN_ROLE_ID, CONTRIBUTOR_ROLE_ID } from "../constants/userRoles";
import PreviewFormInstance from "../components/Form/PreviewFormInstance";
import getclassNames from "../utils/getClassNames";
import Program from "../models/serviceModels/Program";
import { getEnrolledPrograms, getFormsByProgramId } from "../services/programsService";
import WarningMessages from "../components/UI/WarnginMessages";
import LoadingPlaceholder from "../components/UI/LoadingPlaceholder";
import EditableFormField from "../models/pageModels/EditableFormField";
import { mapFormFieldToEditableFormField, normalizeFormInstanceFieldValue } from "../mappers/formFieldMapper";
import FormEditor from "../components/Form/Editor/FormEditor";
import { UPLOADED } from "../constants/fileUploadStatus";
import Client from "../models/serviceModels/Client";
import toast from "react-hot-toast";
import Input from "../components/UI/Input";
import HouseholdTrackingControls from "../components/Tracking/HouseholdTrackingControls";
import HouseholdTrackingProgress from "../components/Tracking/HouseholdTrackingProgress";
import HouseholdTrackingSummary from "../components/Tracking/HouseholdTrackingSummary";
import { bulkHouseholdTrackingEnabled } from "../utils/features";
import {
    buildHouseholdTrackingGroups,
    buildHouseholdTrackingTargets,
    getSelectableHouseholdTrackingClientIds,
    getHouseholdTrackingIneligiblePeopleCount,
    getHouseholdTrackingReviewedPeopleCount,
    resolveHouseholdTrackingEligibility,
    runHouseholdTrackingSubmissions
} from "../utils/householdTracking";
import {
    HouseholdTrackingEligibility,
    HouseholdTrackingProgress as HouseholdTrackingProgressModel,
    HouseholdTrackingSummary as HouseholdTrackingSummaryModel
} from "../models/pageModels/HouseholdTracking";

const TAKE = 10;

const ParticipantTracking = () => {
    const { id } = useParams();
    const [client] = useOutletContext<[Client]>();
    const [paging, setPaging] = useState(false);
    const [loading, setLoading] = useState(false);
    const [forms, setForms] = useState<Form[]>();
    const [instances, setInstances] = useState<FormInstance[]>();
    const [previewInstance, setPreviewInstance] = useState<FormInstance>();
    const [highlightInstance, setHighlightInstance] = useState<FormInstance>();
    const [totalInstances, setTotalInstances] = useState<number>();
    const [skip, setSkip] = useState<number>(0);
    const [apiErrors, setApiErrors] = useState<any>();
    const [submitValidationErrors, setSubmitValidationErrors] = useState<string[]>([]);
    const [formTypeId, setFormTypeId] = useState('');
    const [formId, setFormId] = useState('');
    const [searching, setSearching] = useState(false);
    const loaded = useRef(false);
    const [reference, visible] = useElementVisible<HTMLDivElement>();
    const [filteredForms, setFilteredForms] = useState<Form[]>([]);
    const [searchFields, setSearchFields] = useState<SearchFieldReference[]>([]);
    const [searchFilters, setSearchFilters] = useState<DefaultSearchFilter[]>([]);
    const [searchEvent, setSearchEvent] = useState<SearchEvent>();
    const [confirmInstanceDeletion, setConfirmInstanceDeletion] = useState<FormInstance>();
    const [modalErrors, setModalErrors] = useState<any>();
    const [deleting, setDeleting] = useState(false);
    const me = useContext(MeContext);
    const [programs, setPrograms] = useState<Program[]>([]);
    const [selectedProgram, setSelectedProgram] = useState<Program>();
    const [selectedForm, setSelectedForm] = useState<Form>();
    const [availableForms, setAvailableForms] = useState<Form[]>([]);
    const [loadingForms, setLoadingForms] = useState(false);
    const [loadingSelectedForm, setLoadingSelectedForm] = useState(false);
    const [editableFields, setEditableFields] = useState<EditableFormField[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [submittingAndResetting, setSubmittingAndResetting] = useState(false);
    const [submitForHousehold, setSubmitForHousehold] = useState(false);
    const [selectedHouseholdClientIds, setSelectedHouseholdClientIds] = useState<string[]>([]);
    const [householdEligibility, setHouseholdEligibility] = useState<Record<string, HouseholdTrackingEligibility>>({});
    const [loadingHouseholdEligibility, setLoadingHouseholdEligibility] = useState(false);
    const [householdProgress, setHouseholdProgress] = useState<HouseholdTrackingProgressModel>();
    const [householdSummary, setHouseholdSummary] = useState<HouseholdTrackingSummaryModel>();
    const householdEligibilityRequest = useRef(0);
    const householdTrackingGroups = useMemo(
        () => client ? buildHouseholdTrackingGroups(client) : [],
        [client]
    );

    useEffect(() => {
        setLoading(true);
        if (id) {
            Promise.all([executeSearch<FormInstance>({
                searchTypeId: TRACKING_HISTORY_SEARCH,
                fields: [],
                disabledFilters: [],
                skip,
                clientId: id,
                take: TAKE
            }), getSearchFields(TRACKING_HISTORY_SEARCH, undefined, true),
            getDefaultSearchFilters(TRACKING_HISTORY_SEARCH),
            getEnrolledPrograms(id)]).then(([fo, fi, df, ep]) => {
                setSearchFields(fi);
                setSearchFilters(df);
                setInstances(fo.data);
                setTotalInstances(fo.totalCount);
                setPrograms(ep);
                loaded.current = true;
            }).catch(e => {
                console.error(e);
                setApiErrors(e);
            }).finally(() => {
                setLoading(false);
            });
        }
    }, [id]);

    useEffect(() => {
        setFormId('');

        if (!forms || !formTypeId || isNaN(Number(formTypeId))) {
            return;
        }

        setFilteredForms([
            ...forms.filter(f => f.formTypeId === Number(formTypeId))
        ]);
    }, [formTypeId, forms]);

    useEffect(() => {
        if (visible) {
            setSkip(current => {
                return current + TAKE;
            });
        }
    }, [visible]);

    const handleOnProgramChange = (programId?: string) => {
        resetHouseholdTracking();
        const matchingProgram = programs.find(p => p.id === programId);
        setSelectedProgram(matchingProgram);
        setSelectedForm(undefined);
        setEditableFields([]);
        setApiErrors(undefined);
        setAvailableForms([]);

        if (matchingProgram) {
            setLoadingForms(true);
            getFormsByProgramId(matchingProgram.id).then(f => {
                setAvailableForms(f);
            }).catch(e => {
                console.error(e);
                setApiErrors(e);
            }).finally(() => {
                setLoadingForms(false);
            });
        }
    };

    const handleSetSelectedForm = (formId?: string) => {
        resetHouseholdTracking();
        const matchingForm = availableForms.find(f => f.id === formId);
        setSelectedForm(matchingForm);
        setApiErrors(undefined);

        if (matchingForm) {
            setLoadingSelectedForm(true);

            getForm(matchingForm.id, matchingForm.versionId).then(lf => {
                setEditableFields(lf.fields.map(ff => mapFormFieldToEditableFormField(ff)));
            }).catch(e => {
                console.error(e);
                setApiErrors(e);
            }).finally(() => {
                setLoadingSelectedForm(false);
            });
        }
    };

    const executeFormSearch = useCallback((request: { skip: number, formTypeId?: string, formId?: string }) => {
        setApiErrors(undefined);
        if (!id) {
            return new Promise<any>((_, reject) => {
                reject('Cannot execute search without a participant ID')
            });
        }

        return searchFormsInstances({
            skip: request.skip,
            formTypeId: request.formTypeId && !isNaN(Number(request.formTypeId)) ? Number(request.formTypeId) : undefined,
            formId: request.formId,
            clientId: id
        });
    }, [id]);

    useEffect(() => {
        if (!loaded.current) {
            return;
        }

        setPaging(true);
        setApiErrors(undefined);

        executeSearch<FormInstance>({
            fields: searchEvent?.fields?.map(f => {
                return {
                    id: f.fieldId,
                    filterTypeId: f.filterTypeId,
                    value: f.value
                }
            }) || [],
            disabledFilters: searchEvent?.disabledFilters || [],
            skip: skip,
            take: TAKE,
            clientId: id,
            searchTypeId: TRACKING_HISTORY_SEARCH,
            query: searchEvent?.query || ''
        }).then(r => {
            setInstances(current => {
                return current ? [
                    ...current,
                    ...r.data
                ] : r.data;
            });
            setTotalInstances(r.totalCount);
        }).catch(e => {
            console.error(e);
            setApiErrors(e);
        }).finally(() => {
            setPaging(false);
        });
    }, [skip, executeFormSearch]);

    useEffect(() => {
        if (!highlightInstance) {
            return;
        }

        const timeoutReference = setTimeout(() => {
            setHighlightInstance(undefined);
        }, 3000);

        return () => {
            if (timeoutReference) {
                clearTimeout(timeoutReference);
            }
        }
    }, [highlightInstance]);

    const handleOnSearchV2 = (e: SearchEvent) => {
        setSkip(0);
        setSearching(true);
        setSearchEvent(e);
        setApiErrors(undefined);

        executeSearch<FormInstance>({
            fields: e.fields?.map(f => {
                return {
                    id: f.fieldId,
                    filterTypeId: f.filterTypeId,
                    value: f.value
                }
            }) || [],
            disabledFilters: e.disabledFilters,
            skip: 0,
            take: TAKE,
            clientId: id,
            searchTypeId: TRACKING_HISTORY_SEARCH,
            query: e.query
        }).then(r => {
            setInstances(r.data);
            setTotalInstances(r.totalCount);
        }).catch(e => {
            console.error(e);
            setApiErrors(e);
        }).finally(() => {
            setSearching(false);
        });
    };

    const handleOnClosePreview = () => {
        setHighlightInstance(previewInstance);
        setPreviewInstance(undefined);
    };

    const handleDeletion = () => {
        if (!confirmInstanceDeletion) {
            return;
        }

        setDeleting(true);

        deleteFormInstance(confirmInstanceDeletion.formId, confirmInstanceDeletion.id).then(() => {
            setConfirmInstanceDeletion(undefined);
            handleOnSearchV2(searchEvent || {
                fields: [],
                disabledFilters: []
            });
        }).catch(e => {
            console.error(e);
            setModalErrors(e);
        }).finally(() => {
            setDeleting(false);
        });
    };

    const handleConfirmDeletion = (instance: FormInstance) => {
        setConfirmInstanceDeletion(instance);
        setModalErrors(undefined);
        setDeleting(false);
    };


    const handleFormReset = () => {
        setEditableFields([
            ...editableFields.map(f => {
                return {
                    ...f,
                    value: normalizeFormInstanceFieldValue(f),
                    files: []
                }
            })
        ]);
    };

    function resetHouseholdTracking() {
        householdEligibilityRequest.current += 1;
        setSubmitForHousehold(false);
        setSelectedHouseholdClientIds([]);
        setHouseholdEligibility({});
        setLoadingHouseholdEligibility(false);
        setHouseholdProgress(undefined);
        setHouseholdSummary(undefined);
    }

    const handleSubmitForHouseholdChange = async (checked: boolean) => {
        householdEligibilityRequest.current += 1;
        const requestId = householdEligibilityRequest.current;
        setSubmitForHousehold(checked);
        setHouseholdSummary(undefined);
        setHouseholdProgress(undefined);

        if (!checked || !selectedProgram) {
            setHouseholdEligibility({});
            setSelectedHouseholdClientIds([]);
            setLoadingHouseholdEligibility(false);
            return;
        }

        const targets = buildHouseholdTrackingTargets(client, householdTrackingGroups);
        setHouseholdEligibility(targets.reduce<Record<string, HouseholdTrackingEligibility>>((result, target) => {
            result[target.clientId] = target.clientId === client.id
                ? { status: 'eligible' }
                : { status: 'checking' };
            return result;
        }, {}));
        setLoadingHouseholdEligibility(true);

        const eligibility = await resolveHouseholdTrackingEligibility(
            targets,
            client.id,
            selectedProgram,
            getEnrolledPrograms
        );

        if (householdEligibilityRequest.current !== requestId) {
            return;
        }

        setHouseholdEligibility(eligibility);
        setSelectedHouseholdClientIds(getSelectableHouseholdTrackingClientIds(
            targets,
            eligibility
        ));
        setLoadingHouseholdEligibility(false);
    };

    const handleSubmitForm = async (e: React.FormEvent) => {
        e?.preventDefault();
        setApiErrors(undefined);
        setSubmitValidationErrors([]);

        if (!selectedForm?.id) {
            return;
        }

        setSubmitting(true);

        const createRequest = (clientId: string) => createFormInstance({
            formId: selectedForm.id,
            versionId: selectedForm.versionId,
            clientId,
            fields: editableFields.map(f => {
                return {
                    fieldId: f.id,
                    value: normalizeFormInstanceFieldValue(f, f.value),
                    files: f.files?.map(f => f.fileId!) || []
                }
            })
        });

        if (!submitForHousehold) {
            try {
                await createRequest(id!);
                handleFormReset();
                toast.success("Successfully submitted form");
            } catch (error) {
                setApiErrors(error);
                console.error(error);
                toast.error("Failed to submit form");
            } finally {
                setSubmitting(false);
            }
            return;
        }

        const allTargets = buildHouseholdTrackingTargets(client, householdTrackingGroups);
        const selectedClientIds = new Set(selectedHouseholdClientIds);
        const eligibleTargets = allTargets.filter(target =>
            householdEligibility[target.clientId]?.status === 'eligible'
            && selectedClientIds.has(target.clientId)
        );
        if (eligibleTargets.length === 0) {
            setSubmitValidationErrors(['Select at least one eligible participant for household tracking submission']);
            setSubmitting(false);
            return;
        }

        let completed = 0;
        setHouseholdSummary(undefined);
        setHouseholdProgress({
            completed: 0,
            total: eligibleTargets.length
        });

        const results = await runHouseholdTrackingSubmissions(
            eligibleTargets,
            target => createRequest(target.clientId),
            {
                concurrency: 3,
                retries: 3,
                baseDelayMs: 1000,
                onJobUpdate: (target, status, attempt) => {
                    setHouseholdProgress(current => ({
                        completed: current?.completed || completed,
                        total: eligibleTargets.length,
                        activeName: target.name,
                        activeAttempt: attempt,
                        activeStatus: status
                    }));
                },
                onJobComplete: () => {
                    completed += 1;
                    setHouseholdProgress(current => ({
                        completed,
                        total: eligibleTargets.length,
                        activeName: current?.activeName,
                        activeAttempt: current?.activeAttempt,
                        activeStatus: current?.activeStatus
                    }));
                }
            }
        );

        const succeeded = results.filter(result => result.status === 'succeeded').length;
        const failed = results.length - succeeded;
        setHouseholdProgress(undefined);
        setHouseholdSummary({
            totalPeopleReviewed: getHouseholdTrackingReviewedPeopleCount(client, householdTrackingGroups),
            eligibleParticipants: eligibleTargets.length,
            succeeded,
            failed,
            ineligible: getHouseholdTrackingIneligiblePeopleCount(householdTrackingGroups, householdEligibility),
            results
        });
        handleFormReset();
        setSubmitting(false);

        if (failed === 0) {
            toast.success(`Successfully submitted form for ${succeeded} participant${succeeded === 1 ? '' : 's'}`);
        } else {
            toast.error(`Submission completed with ${failed} failure${failed === 1 ? '' : 's'}`);
        }
    };


    const handleOnFieldsChange = (fields: EditableFormField[]) => {
        setEditableFields([
            ...fields
        ]);
    };

    const getMainContent = () => {
        if (loading) {
            return <Loader />
        }

        const getActivityDate = (instance: FormInstance) => {
            if (instance.activityDate) {
                return instance.activityDate;
            }

            const activityDateField = instance.instanceFields?.find(fi => fi.fieldTypeId === ACTIVITY_DATE_ID);
            if (!activityDateField) {
                return null;
            }

            return activityDateField.value;
        };

        const getFormTypeName = (formTypeId: number) => {
            if (formTypeId === ASSESSMENT_ID) {
                return 'Assessment - ';
            }

            if (formTypeId === DELIVERY_ID) {
                return 'Outputs & Outcomes - '
            }

            console.warn("Not sure what this form type is:", formTypeId)
            return "";
        };

        const handleOnPrint = () => {
            window.print();
        };

        const getParticipantName = () => {
            return `${client.firstName} ${client.lastName}`;
        };

        const clientNameParts = [];
        if (client) {
            clientNameParts.push(client.firstName);
            if (client.middleName) {
                clientNameParts.push(client.middleName);
            }
            clientNameParts.push(client.lastName);
        }
        return (
            <>
                <ErrorMessages messages={submitValidationErrors} />
                <ApiErrorMessages error={apiErrors} />
                <>
                    <div className="hide-on-print">
                        <h3>Track Data</h3>
                        {client && <div>Select a program and then a form to track new data for {clientNameParts.join(" ")}</div>}
                        {programs.length === 0 && <WarningMessages messages={["Participant is not assigned to a program"]} />}
                        {programs.length === 0 && <Button id="assign-to-program" href={`/participants/${id}/programs`} text="Go to Programs" />}
                        <div className="row margin-vertical-5">
                            <div className="column">
                                <Select id="programs"
                                    label="Program"
                                    onChange={handleOnProgramChange}
                                    value={selectedProgram?.id || ''}
                                    disabled={programs.length === 0 || submitting}>
                                    <option value="">Select One</option>
                                    {programs.map(p => {
                                        return <option key={p.id} value={p.id}>{p.name}</option>
                                    })}
                                </Select>
                            </div>
                            <div className="column">
                                {loadingForms && <LoadingPlaceholder id="forms-placeholder" className="margin-0" hideOverlay={true}>
                                    <Select id="forms" label="Form" disabled={true}>
                                        <option value="">Select One</option>
                                    </Select>
                                </LoadingPlaceholder>}
                                {!loadingForms && <Select id="forms"
                                    label="Form"
                                    onChange={handleSetSelectedForm}
                                    value={selectedForm?.id || ''}
                                    disabled={availableForms.length === 0 || submitting}>
                                    <option value="">Select One</option>
                                    {availableForms.map(f => {
                                        return <option key={f.id} value={f.id}>{getFormTypeName(f.formTypeId)}{f.name}</option>
                                    })}
                                </Select>}
                            </div>
                        </div>
                    </div>
                    {loadingSelectedForm && <Loader />}
                    {!loadingSelectedForm && selectedForm && selectedProgram && editableFields.length !== 0 && <>
                        <div className="row justify-content-end hide-on-print">
                            <div className="column-auto">
                                <Button id="print-button"
                                    className="secondary"
                                    text="Print"
                                    disabled={submitting}
                                    onClick={handleOnPrint} />
                            </div>
                        </div>
                        <div className="show-on-print">
                            <h1>{selectedProgram.name} - {selectedForm.name}</h1>
                            <hr />
                        </div>
                        <div className="row">
                            <div className="column">
                                <form onSubmit={handleSubmitForm}>
                                    <div className="row">
                                        <div className="column">
                                            <div className="show-on-print">
                                                <Input id="client-name"
                                                    label="Participant Name"
                                                    inputClassName="margin-bottom-5"
                                                    value={getParticipantName()} />
                                                <hr />
                                            </div>
                                            {bulkHouseholdTrackingEnabled && <div className="hide-on-print">
                                                <HouseholdTrackingControls
                                                    key={`${client.id}-${selectedProgram.id}-${selectedForm.id}`}
                                                    groups={householdTrackingGroups}
                                                    eligibility={householdEligibility}
                                                    loadingEligibility={loadingHouseholdEligibility}
                                                    programName={selectedProgram.name}
                                                    selectedClientIds={selectedHouseholdClientIds}
                                                    checked={submitForHousehold}
                                                    disabled={submitting}
                                                    onChange={handleSubmitForHouseholdChange}
                                                    onSelectedClientIdsChange={setSelectedHouseholdClientIds} />
                                            </div>}
                                            <FormEditor disabled={submitting}
                                                fields={editableFields}
                                                onChange={handleOnFieldsChange} />
                                            {householdProgress && <HouseholdTrackingProgress progress={householdProgress} />}
                                        </div>
                                    </div>
                                    <div className="row margin-top-3 justify-content-between hide-on-print">
                                        <div className="column-auto">
                                            <Button id="reset-button"
                                                className="secondary"
                                                text="Clear Form"
                                                disabled={submitting || loadingHouseholdEligibility}
                                                onClick={handleFormReset} />
                                        </div>
                                        <div className="column-auto">
                                            <div className="row">
                                                <div className="column-auto">
                                                    <Button id="submit-button"
                                                        text={submitForHousehold ? "Submit Form for Selected Participants" : "Submit Form"}
                                                        disabled={submitting || submittingAndResetting || loadingHouseholdEligibility || editableFields.some(f => f.files && f.files.some(ff => ff.status !== UPLOADED))}
                                                        loading={submitting}
                                                        type="submit" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                                {householdSummary && <HouseholdTrackingSummary
                                    summary={householdSummary}
                                    groups={householdTrackingGroups}
                                    eligibility={householdEligibility} />}
                            </div>
                        </div>
                    </>
                    }
                </>
                <div className="hide-on-print">
                    <hr />
                    <h3>Tracking History</h3>
                    {instances && (<>
                        <Search
                            id={`search-tracking-history`}
                            searching={searching}
                            fields={searchFields}
                            defaultFilters={searchFilters}
                            onSearch={handleOnSearchV2}
                            lockedOpen={true} />
                        <div className="list-item-container">
                            {instances.map(i => {
                                const options: { label: string, href?: string, onClick?: () => void, newWindow?: boolean; }[] = [
                                    {
                                        label: 'View',
                                        href: `/forms/${i.formId}/versions/${i.formVersionId}/instances/${i.id}/view`
                                    }
                                ];

                                options.push({
                                    label: 'Preview',
                                    onClick: () => setPreviewInstance(i)
                                });

                                if (me.user?.userRoleId === ADMIN_ROLE_ID || me.user?.userRoleId === CONTRIBUTOR_ROLE_ID) {
                                    options.push({
                                        label: 'Edit',
                                        href: `/forms/${i.formId}/versions/${i.formVersionId}/instances/${i.id}/edit`
                                    });
                                    options.push({
                                        label: 'Delete',
                                        onClick: () => handleConfirmDeletion(i)
                                    });
                                }
                                const activityDate = getActivityDate(i);
                                return (<div className={getclassNames('list-item', highlightInstance === i ? 'highlight' : '')}>
                                    <div className="row align-items-center justify-content-between">
                                        <div className="column-medium-4">
                                            <div><strong>{i.formName}</strong></div>
                                            <div>{Mapping[i.formTypeId]}</div>
                                        </div>
                                        <div className="column-medium-3">
                                            <div><small>Originally Submitted: {format(new Date(i.created), 'MM/dd/yyyy')}</small></div>
                                            {i.updated && <div><small>Last Updated: {i.updated && format(new Date(i.updated), 'MM/dd/yyyy')}</small></div>}
                                            {activityDate && <div><small>Activity Date: {formatJSONDateWithoutTime(activityDate, 'MM/dd/yyyy')}</small></div>}
                                        </div>
                                        <div className="column">
                                            <div className="row justify-content-end">
                                                <div className="column-auto">
                                                    <Dropdown id={`dropdown-instances-${i.id}`}
                                                        options={options} />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>);

                            })}
                        </div>
                        {paging && <Loader />}
                        {instances && totalInstances && instances.length < totalInstances && <div ref={reference} className="padding-5 margin-bottom-5"></div>}
                    </>)
                    }
                </div>

                <Modal id="preview-modal" visible={previewInstance !== undefined} className="medium" title="Preview Form Submission" onClose={handleOnClosePreview}>
                    {previewInstance && <>
                        <PreviewFormInstance formId={previewInstance.formId}
                            instanceId={previewInstance.id}
                            versionId={previewInstance.formVersionId} />
                        <div className="row margin-top-5">
                            <div className="column">
                                <div className="row justify-content-end">
                                    <div className="column-auto">
                                        <Button id="cancel-delete"
                                            text="Close"
                                            className="secondary"
                                            onClick={handleOnClosePreview} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>}
                </Modal>
                <Modal id="delete-modal" visible={confirmInstanceDeletion !== undefined} className="small" title="Confirm Delete Form Submission" onClose={() => setConfirmInstanceDeletion(undefined)}>
                    {confirmInstanceDeletion && <>
                        <ApiErrorMessages error={modalErrors} />
                        <div className="row">
                            <div className="column">
                                <div>Are you sure you want to delete this Form Submission?</div>
                                <br />
                                <div>This action is irreversible.</div>
                                <br />
                                <div>Form Name: {confirmInstanceDeletion.formName}</div>
                                <div>Activity Date: {(confirmInstanceDeletion.activityDate && ` - ${formatJSONDateWithoutTime(confirmInstanceDeletion.activityDate, 'MM/dd/yyyy')}`) || 'Not Set'}</div>
                            </div>
                        </div>
                        <div className="row margin-top-5">
                            <div className="column">
                                <div className="row justify-content-between">
                                    <div className="column-auto">
                                        <Button id="cancel-delete"
                                            text="Cancel"
                                            className="secondary"
                                            disabled={deleting}
                                            onClick={() => setConfirmInstanceDeletion(undefined)} />
                                    </div>
                                    <div className="column-auto">
                                        <Button id="execute-delete"
                                            text="Delete"
                                            className="warning"
                                            disabled={deleting}
                                            loading={deleting}
                                            onClick={handleDeletion} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>}
                </Modal>
            </>
        );
    };

    return getMainContent();
};

export default ParticipantTracking;
